import { useState, useEffect } from 'react';
import { Form, Button, Card } from 'react-bootstrap';
import Select from 'react-select';
import axios from 'axios';
import config from '../../config';
import { toast } from 'react-toastify';

function PurchaseRequestUpdateForm({ initialData, onSubmit }) {
    const [form, setForm] = useState({
        required_by_date: '',
        reason: '',
        priority: 'medium',
        notes: '',
        items: [{ product: '', quality: '', approx_quantity: '', quantity_unit: '' }],
    });

    const [stepSubmitted, setStepSubmitted] = useState(false);
    const [productOptions, setProductOptions] = useState([]);
    const [productMap, setProductMap] = useState({});

    //   useEffect(() => {
    //   if (initialData) {
    //     setForm({
    //       ...initialData,
    //       required_by_date: new Date(initialData.required_by_date).toISOString().split('T')[0],
    //     });
    //   }
    // }, [initialData]);

    useEffect(() => {
        if (initialData) {
            setForm({
                _id: initialData._id,
                required_by_date: new Date(initialData.required_by_date).toISOString().split('T')[0], // '2025-08-06'
                reason: initialData.reason,
                status: initialData.status,
                requested_by: initialData.requested_by._id, // just the ID
                priority: initialData.priority,
                notes: initialData.notes,
                items: initialData.items.map(item => ({
                    _id: item._id,
                    product: {
                        value: item.product._id,
                        label: item.product.productName,
                    },           // for dropdown value
                    productName: item.product.productName, // optional for display
                    quality: item.quality,
                    approx_quantity: item.approx_quantity,
                    quantity_unit: item.quantity_unit,
                }))
            });
        }
    }, [initialData]);

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const res = await axios.get(`${config.API_BASE_URL}/products?populateUnit=true`);
                const options = res.data.map(p => ({
                    label: p.productName,
                    value: p._id,
                }));

                const map = {};
                res.data.forEach(p => {
                    map[p._id] = {
                        unitName: p.unitId?.unitCode || '',
                        fullProduct: p,
                    };
                });

                setProductOptions(options);
                setProductMap(map);
            } catch (err) {
                toast.error('Failed to load products');
            }
        };

        fetchProducts();
    }, []);

    const handleItemChange = (index, field, value) => {
        const updatedItems = [...form.items];
        updatedItems[index][field] = value;
        setForm({ ...form, items: updatedItems });
    };

    const handleProductSelect = (index, selectedOption) => {
        const updatedItems = [...form.items];
        updatedItems[index].product = selectedOption;
        updatedItems[index].quantity_unit = productMap[selectedOption.value]?.unitName || '';
        setForm({ ...form, items: updatedItems });
    };

    const addItem = () => {
        setForm({
            ...form,
            items: [...form.items, { product: null, quality: '', approx_quantity: '', quantity_unit: '' }],
        });
    };

    const removeItem = (index) => {
        if (form.items.length <= 1) return;
        const updatedItems = [...form.items];
        updatedItems.splice(index, 1);
        setForm({ ...form, items: updatedItems });
    };

    const validateForm = () => {
        const basicValid = form.required_by_date && form.reason;
        const itemsValid = form.items.length > 0 &&
            form.items.every(item => item.product?.value && item.approx_quantity && item.quantity_unit);
        return basicValid && itemsValid;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setStepSubmitted(true);
        if (validateForm()) {
            const formattedData = {
                ...form,
                items: form.items
                    .filter(item => item.product?.value)  // avoid undefined/null entries
                    .map(item => ({
                        ...item,
                        product: item.product.value,
                    })),
            };
            console.log('Submitting payload:', formattedData);
onSubmit(formattedData);
        } else {
            toast.error('Please fill all required fields.');
        }
    };

    return (
        <Form onSubmit={handleSubmit}>
            <Card className="mb-4 p-3">
                <h5 className="mb-3">Request Info</h5>
                <div className="d-flex flex-column flex-sm-row gap-2 align-items-start align-items-sm-center flex-wrap mb-3">
                    <div style={{ flex: 1.2, minWidth: '200px' }}>
                        <Form.Group>
                            <Form.Label>Required By Date</Form.Label>
                            <Form.Control
                                type="date"
                                value={form.required_by_date}
                                onChange={(e) => setForm({ ...form, required_by_date: e.target.value })}
                                isInvalid={stepSubmitted && !form.required_by_date}
                            />
                        </Form.Group>
                    </div>

                    <div style={{ flex: 1.4, minWidth: '170px' }}>
                        <Form.Group>
                            <Form.Label>Reason</Form.Label>
                            <Form.Select
                                value={form.reason}
                                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                                isInvalid={stepSubmitted && !form.reason}
                            >
                                <option value="">Select reason</option>
                                <option value="low stock">Low Stock</option>
                                <option value="urgent requirement">Urgent Requirement</option>
                            </Form.Select>
                        </Form.Group>
                    </div>

                    <div style={{ flex: 1.4, minWidth: '170px' }}>
                        <Form.Group>
                            <Form.Label>Priority</Form.Label>
                            <Form.Select
                                value={form.priority}
                                onChange={(e) => setForm({ ...form, priority: e.target.value })}
                            >
                                <option value="high">High</option>
                                <option value="medium">Medium</option>
                                <option value="low">Low</option>
                            </Form.Select>
                        </Form.Group>
                    </div>
                </div>

                <Form.Group>
                    <Form.Label>Notes</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        value={form.notes}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    />
                </Form.Group>
            </Card>

            <Card className="mb-4 p-3">
                <h5 className="mb-3">Requested Items</h5>

                {form.items.map((item, index) => (
                    <div key={index} className="border rounded mb-3 p-3 pt-2 position-relative">
                        <div className="d-flex flex-column flex-sm-row gap-2 align-items-start align-items-sm-center flex-wrap">
                            <div style={{ flex: 2, minWidth: '150px' }}>
                                <Form.Group>
                                    <Form.Label>Product</Form.Label>
                                    <Select
                                        options={productOptions}
                                        value={item.product}
                                        onChange={(selected) => handleProductSelect(index, selected)}
                                        className={stepSubmitted && !item.product ? 'is-invalid' : ''}
                                    />
                                </Form.Group>
                            </div>

                            <div style={{ flex: 1.2, minWidth: '120px' }}>
                                <Form.Group>
                                    <Form.Label>Quality</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={item.quality}
                                        onChange={(e) => handleItemChange(index, 'quality', e.target.value)}
                                    />
                                </Form.Group>
                            </div>

                            <div style={{ flex: 1.2, minWidth: '120px', position: 'relative' }}>
                                <Form.Group>
                                    <Form.Label>Quantity</Form.Label>
                                    <Form.Control
                                        type="number"
                                        value={item.approx_quantity}
                                        onChange={(e) => handleItemChange(index, 'approx_quantity', e.target.value)}
                                        isInvalid={stepSubmitted && !item.approx_quantity}
                                        style={{ paddingRight: '40px' }}
                                    />
                                    {item.quantity_unit && (
                                        <span className="position-absolute top-50 end-0 translate-middle-y me-3 small bg-light border px-2 rounded">
                                            {item.quantity_unit}
                                        </span>
                                    )}
                                </Form.Group>
                            </div>

                            <div style={{ flex: '0 0 auto', marginTop: '30px' }}>
                                {form.items.length > 1 && (
                                    <Button variant="outline-danger" size="sm" onClick={() => removeItem(index)}>
                                        Remove
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                ))}

                <Button variant="outline-primary" onClick={addItem}>
                    + Add Item
                </Button>
            </Card>

            <div className="text-end">
                <Button variant="success" type="submit">
                    Update Request
                </Button>
            </div>
        </Form>
    );
}

export default PurchaseRequestUpdateForm;
